import type { IconName } from '@customerio/pluma-icons';
import {
  channelValueOf,
  getDeliveryChannel,
  isDeliveryStateEnabledForChannel,
  stateColor,
  stateDescription,
  stateLabel,
} from '@/features/deliveries';
import type { Delivery } from '@/services/db/delivery/entity';
import { deliveryStateValue, isEmail } from '@/services/db/delivery/utils';

export type DeliveryTimelineAttribution = 'human' | 'machine';

export type DeliveryTimelineColor = 'blue' | 'green' | 'grey' | 'red' | 'yellow';

// Chronological tie-break order: when two events share a timestamp, the earlier
// entry here renders first.
const DELIVERY_TIMELINE_EVENT_KEYS = [
  'created',
  'drafted',
  'attempted',
  'deferred',
  'undeliverable',
  'failed',
  'sent',
  'bounced',
  'suppressed',
  'delivered',
  'holdout',
  'canceled',
  'viewed',
  'prefetch_opened',
  'machine_clicked',
  'opened',
  'human_opened',
  'human_clicked',
  'clicked',
  'replied',
  'converted',
  'unsubscribed',
  'topic_unsubscribed',
  'spammed',
] as const;

export type DeliveryTimelineEventKey = (typeof DELIVERY_TIMELINE_EVENT_KEYS)[number];

export interface DeliveryTimelineEvent {
  key: DeliveryTimelineEventKey;
  label: string;
  description: string;
  timestamp: number;
  color: DeliveryTimelineColor;
  icon: IconName;
  attribution: DeliveryTimelineAttribution | null;
}

interface DeliveryTimelineEventDefinition {
  icon: IconName;
  attribution?: DeliveryTimelineAttribution;
  // Overrides below take precedence over the delivery-state defaults. Colors
  // follow Pluma Timeline semantics: blue for informational delivery steps,
  // yellow for machine-attributed events, green for human engagement.
  color?: DeliveryTimelineColor;
  label?: string;
  description?: string;
}

const EVENT_DEFINITIONS: Record<DeliveryTimelineEventKey, DeliveryTimelineEventDefinition> = {
  created: { icon: 'trigger-event' },
  drafted: { icon: 'draft' },
  attempted: { icon: 'caution' },
  deferred: { icon: 'time-delay' },
  undeliverable: {
    icon: 'does-not-send',
    description: "We couldn't deliver this message. Check its error details for the reason.",
  },
  failed: { icon: 'critical' },
  sent: { icon: 'email', color: 'blue' },
  bounced: { icon: 'critical' },
  suppressed: { icon: 'does-not-send' },
  delivered: { icon: 'check-circle', color: 'blue' },
  holdout: { icon: 'does-not-send' },
  canceled: { icon: 'stop-circle' },
  viewed: { icon: 'view-on', color: 'blue' },
  prefetch_opened: {
    icon: 'view-on',
    attribution: 'machine',
    color: 'yellow',
    description: 'Mailbox security scanned the message before a person engaged.',
  },
  machine_clicked: {
    icon: 'cursor',
    attribution: 'machine',
    color: 'yellow',
    description: 'Mailbox security followed a tracked link before a person engaged.',
  },
  opened: { icon: 'view-on' },
  human_opened: { icon: 'person', attribution: 'human' },
  human_clicked: { icon: 'cursor', attribution: 'human' },
  clicked: { icon: 'cursor' },
  replied: { icon: 'chat' },
  converted: { icon: 'goal' },
  unsubscribed: { icon: 'unsubscribe' },
  topic_unsubscribed: { icon: 'unsubscribe', label: 'Topic unsubscribed' },
  spammed: { icon: 'caution' },
};

const VALID_COLORS: ReadonlySet<string> = new Set(['blue', 'green', 'grey', 'red', 'yellow']);

function isTimelineColor(color: string): color is DeliveryTimelineColor {
  return VALID_COLORS.has(color);
}

// Pluma content guidelines require sentence case, so the state map's title-case
// labels (e.g. "Machine Opened") become "Machine opened".
function toSentenceCase(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

function labelFor(key: DeliveryTimelineEventKey): string {
  return EVENT_DEFINITIONS[key].label ?? toSentenceCase(stateLabel(key));
}

function descriptionFor(key: DeliveryTimelineEventKey, channel: string): string {
  return EVENT_DEFINITIONS[key].description ?? stateDescription(key, channel) ?? '';
}

function colorFor(key: DeliveryTimelineEventKey): DeliveryTimelineColor {
  const override = EVENT_DEFINITIONS[key].color;
  if (override != null) {
    return override;
  }
  const color = stateColor(key);
  return isTimelineColor(color) ? color : 'grey';
}

function iconFor(key: DeliveryTimelineEventKey, channel: string): IconName {
  if (key === 'sent') {
    return getDeliveryChannel(channel)?.icon ?? EVENT_DEFINITIONS.sent.icon;
  }
  return EVENT_DEFINITIONS[key].icon;
}

function isEventEnabled(key: DeliveryTimelineEventKey, channel: string): boolean {
  return isDeliveryStateEnabledForChannel(key, channel);
}

// Email is the only channel that reports the human/machine engagement
// breakdown; its metric lists gain these keys on top of the shared catalog.
const EMAIL_ATTRIBUTED_KEYS = ['human_opened', 'human_clicked', 'prefetch_opened', 'machine_clicked'] as const;
const EMAIL_ATTRIBUTED_KEY_SET: ReadonlySet<string> = new Set(EMAIL_ATTRIBUTED_KEYS);

// The `metrics` / `archived_metrics` maps are opaque string maps (the generated
// schema types them as `{ [key]: string }`) that mix count- and timestamp-shaped
// values, so a raw value can't be trusted as a timestamp. A value is only usable
// on the timeline if it sits within the delivery's own lifetime: reject anything
// before `created` or absurdly far after it, so a count-shaped value (e.g. "2")
// reads as absent rather than rendering at 1 Jan 1970. Same bounds check the
// builder-sandbox first-send timeline applies to these metric reads.
const MAX_DELIVERY_METRIC_ELAPSED_SECONDS = 30 * 24 * 60 * 60;

function parseMetricTimestamp(value: string | undefined, created: number): number | null {
  if (value == null) {
    return null;
  }
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    return null;
  }
  if (timestamp < created || timestamp > created + MAX_DELIVERY_METRIC_ELAPSED_SECONDS) {
    return null;
  }
  return timestamp;
}

// The attributed (human/machine) keys are sourced from the ambiguous `metrics` /
// `archived_metrics` maps and validated against `created`; every other key is a
// numeric top-level timestamp attr (`opened`, `sent`, `viewed`, …) read as-is.
// `viewed` and `topic_unsubscribed` are top-level wire fields the generated
// OpenAPI type omits, so `deliveryStateValue`'s record read surfaces them when
// present and yields 0 (dropped) when not — never a 1970 row.
function readTimestamp(delivery: Delivery, key: DeliveryTimelineEventKey): number | null {
  if (EMAIL_ATTRIBUTED_KEY_SET.has(key)) {
    if (!isEmail(delivery)) {
      return null;
    }
    const created = delivery.created ?? 0;
    return (
      parseMetricTimestamp(delivery.metrics?.[key], created) ??
      parseMetricTimestamp(delivery.archived_metrics?.[key], created)
    );
  }
  const value = deliveryStateValue(delivery, key);
  return value > 0 ? value : null;
}

function baseCandidates(delivery: Delivery): Set<string> {
  const channelInfo = getDeliveryChannel(channelValueOf(delivery.action_type));
  const eventLists = [channelInfo?.deliveryStates, channelInfo?.deliveryActions, channelInfo?.deliveryMetrics];
  const candidates = new Set<string>(['created', 'canceled', ...eventLists.flatMap((list) => list ?? [])]);
  if (isEmail(delivery)) {
    for (const key of EMAIL_ATTRIBUTED_KEYS) {
      candidates.add(key);
    }
  }
  return candidates;
}

function hasAttributedOpen(delivery: Delivery): boolean {
  return readTimestamp(delivery, 'human_opened') != null || readTimestamp(delivery, 'prefetch_opened') != null;
}

function hasAttributedClick(delivery: Delivery): boolean {
  return readTimestamp(delivery, 'human_clicked') != null || readTimestamp(delivery, 'machine_clicked') != null;
}

function candidateKeys(delivery: Delivery, channel: string): DeliveryTimelineEventKey[] {
  const candidates = baseCandidates(delivery);

  // The plain opened/clicked timestamps duplicate their attributed
  // (human/machine) counterparts, which take priority when present.
  if (hasAttributedOpen(delivery)) {
    candidates.delete('opened');
  }
  if (hasAttributedClick(delivery)) {
    candidates.delete('clicked');
  }

  return DELIVERY_TIMELINE_EVENT_KEYS.filter((key) => candidates.has(key) && isEventEnabled(key, channel));
}

function buildEvent(key: DeliveryTimelineEventKey, timestamp: number, channel: string): DeliveryTimelineEvent {
  return {
    key,
    label: labelFor(key),
    description: descriptionFor(key, channel),
    timestamp,
    color: colorFor(key),
    icon: iconFor(key, channel),
    attribution: EVENT_DEFINITIONS[key].attribution ?? null,
  };
}

function compareEvents(a: DeliveryTimelineEvent, b: DeliveryTimelineEvent): number {
  if (a.timestamp !== b.timestamp) {
    return a.timestamp - b.timestamp;
  }
  return DELIVERY_TIMELINE_EVENT_KEYS.indexOf(a.key) - DELIVERY_TIMELINE_EVENT_KEYS.indexOf(b.key);
}

export function buildDeliveryTimelineEvents(delivery: Delivery): DeliveryTimelineEvent[] {
  const channel = channelValueOf(delivery.action_type);

  const events: DeliveryTimelineEvent[] = [];
  for (const key of candidateKeys(delivery, channel)) {
    const timestamp = readTimestamp(delivery, key);
    if (timestamp != null) {
      events.push(buildEvent(key, timestamp, channel));
    }
  }

  return events.sort(compareEvents);
}
